import "./DatePicker.css";
import "rc-calendar/dist/rc-calendar.css";

// eslint-disable-next-line import/no-extraneous-dependencies
import moment from "moment";
import Calendar from "rc-calendar";
import { ReactNode, useRef } from "react";
import { createPortal } from "react-dom";

import { CalendarDarkIcon } from "../../../../assets";
import { FormLabel } from "../../FormLabel";
import { useDatePicker } from "./useDatePicker";

export interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  isShow?: boolean;
  label?: string;
  allowLabelDangerousHtml?: boolean;
  tooltip?: ReactNode;
  corners?: "default" | "rounded";
  showFormat?: boolean;
  hasError?: boolean;
}

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
const ACCEPTED_DATE_FORMATS = [DISPLAY_DATE_FORMAT, "D/M/YYYY"];

export const DatePicker = ({
  value,
  onChange,
  isShow = false,
  label,
  allowLabelDangerousHtml,
  tooltip,
  corners = "default",
  showFormat = true,
  hasError = false,
}: DatePickerProps): JSX.Element => {
  const calendarPopupRef = useRef<HTMLDivElement>(null);
  const {
    showCalendar,
    selectedDate,
    inputValue,
    calendarPosition,
    datePickerRef,
    handleSelect,
    handleOnChange,
    handleInputChange,
    handleInputKeyDown,
    handleCalendarInputClick,
    handleCalendarInputKeyDown,
  } = useDatePicker({
    isShow,
    value,
    onChange,
    calendarPopupRef,
  });

  const selectedMoment = selectedDate ? moment(selectedDate) : undefined;

  return (
    <div
      ref={datePickerRef}
      className={`DatePicker DatePicker_Corners_${corners} ${hasError ? "DatePicker_HasError" : ""}`}
    >
      <FormLabel label={label} tooltip={tooltip} allowDangerousHtml={allowLabelDangerousHtml} />
      <div
        aria-label="Open calendar"
        role="button"
        tabIndex={0}
        onClick={handleCalendarInputClick}
        onKeyDown={handleCalendarInputKeyDown}
      >
        <CalendarDarkIcon className="CalendarIcon" />
        <div className="rc-calendar-input-wrap">
          <input
            className="rc-calendar-input"
            placeholder="Select date"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleCalendarInputClick}
            onKeyDown={handleInputKeyDown}
          />
        </div>
      </div>
      {showCalendar &&
        createPortal(
          <div
            ref={calendarPopupRef}
            className={`DatePickerDropdown DatePicker_Corners_${corners}`}
            style={{
              left: `${calendarPosition.left}px`,
              top: `${calendarPosition.top}px`,
            }}
          >
            <Calendar
              showToday={false}
              showDateInput={false}
              value={selectedMoment}
              selectedValue={selectedMoment}
              onSelect={handleSelect}
              onChange={handleOnChange}
              format={ACCEPTED_DATE_FORMATS}
            />
          </div>,
          document.body
        )}
      {showFormat && <p className="DateFormatText body2">{DISPLAY_DATE_FORMAT}</p>}
    </div>
  );
};
